import React from 'react';
import { useTranslation } from 'react-i18next';
import { Sparkles, AlertTriangle } from 'lucide-react';
import { useAiCredits } from '../../contexts/AiCreditsContext';
import { estimativaDe, saldoInsuficiente, reais, creditos } from '../../services/aiCreditsService';

interface Props {
  /** Funcionalidade como fica no extrato (rota ou fila): social, ai-generation, image-studio... */
  feature: string;
  /** Ação dentro da funcionalidade: generate-caption, landing-page-chat... */
  action?: string | null;
  /** Quantas vezes a ação roda neste clique (ex.: 4 imagens) */
  quantity?: number;
  /** Rótulo da unidade quando não é "por uso" (ex.: "por execução") */
  unitLabel?: string;
  className?: string;
}

/**
 * Selo de custo ao lado de cada ação de IA: quanto custa em média, quanto já foi gasto e quanto
 * resta no mês, e o botão de recarga (abre por cima da tela, sem perder o que está sendo feito).
 * Saldo que não cobre a ação aparece antes do clique, em vermelho.
 */
export const useAiCost = (feature: string, action: string | null = null, quantity = 1) => {
  const { summary, estimates } = useAiCredits();
  const est = estimativaDe(estimates, feature, action);
  const custo = est.cents * Math.max(1, quantity);
  return { estimate: est, costCents: custo, insufficient: saldoInsuficiente(summary, custo), summary };
};

const AiCostBadge: React.FC<Props> = ({ feature, action = null, quantity = 1, unitLabel, className = '' }) => {
  const { t } = useTranslation();
  const { openRecharge } = useAiCredits();
  const { estimate, costCents, insufficient, summary } = useAiCost(feature, action, quantity);

  const porUso = unitLabel || (quantity > 1 ? t('aiCredits.perClick', 'nesta geração') : t('aiCredits.perUse', 'por uso'));
  const tokens = estimate.tokens ? ` · ~${creditos(estimate.tokens * Math.max(1, quantity))} tokens` : '';

  return (
    <div className={`flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] leading-tight ${insufficient ? 'text-red-600 dark:text-red-400' : 'text-gray-500 dark:text-gray-400'} ${className}`}>
      {insufficient ? <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0" /> : <Sparkles className="h-3.5 w-3.5 flex-shrink-0 text-primary-500" />}
      <span title={t('aiCredits.estimateHint', 'Média dos últimos 30 dias para esta ação (1 crédito = R$ 0,01)')}>
        {t('aiCredits.cost', 'IA: ~{{n}} créditos ({{valor}}) {{unidade}}', { n: creditos(costCents), valor: reais(costCents), unidade: porUso })}{tokens}
      </span>
      {summary && (
        <span>
          {summary.postpaid
            ? t('aiCredits.postpaidMonth', '· gasto no mês {{gasto}} (pós-pago)', { gasto: reais(summary.month_spent_cents) })
            : t('aiCredits.availableOf', '· disponível {{disp}} · plano {{resta}} de {{total}} no mês', {
              disp: reais(summary.available_cents),
              resta: reais(summary.included_remaining_cents),
              total: reais(summary.monthly_allowance_cents)
            })}
        </span>
      )}
      {insufficient && <span className="font-medium">{t('aiCredits.insufficient', '· saldo insuficiente para esta ação')}</span>}
      {summary && !summary.billing_active && (
        <span>{t('aiCredits.shadowShort', '· cobrança a partir de {{data}}', { data: new Date(summary.billing_start).toLocaleDateString('pt-BR') })}</span>
      )}
      {!summary?.postpaid && (
        <button type="button" onClick={openRecharge} className="font-medium text-primary-600 dark:text-primary-400 hover:underline">
          {t('aiCredits.recharge', 'Recarregar')}
        </button>
      )}
    </div>
  );
};

export default AiCostBadge;
